import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import Card from '../common/Card';
import Skeleton from '../common/Skeleton';
import GradeStamp from '../common/GradeStamp';
import GradingLoader from '../common/GradingLoader';
import { pickScoreToGrade, gradeToColor } from '../../utils/gradeEngine';
import { V1DraftPick, V1DraftAnalysis, LeagueDraftAnalytics, V1Standings } from '../../types';
import { v1Api } from '../../services/api';
import BestDraftersContainer from './BestDraftersContainer';

interface DraftBoardProps {
  leagueId: number;
  onPlayerSelect?: (playerId: number) => void;
  onTeamSelect?: (teamId: number) => void;
}

// Data-encoding palette (matches DESIGN.md / tailwind tokens). Never used as full-cell washes.
const PERF = {
  exceeded: '#2FBF71', // Boom / Exceeded / Steal
  met: '#5B8DEF', // Fair / Met
  missed: '#E05252', // Bust / Missed
} as const;

const POSITION_COLOR: { [key: string]: string } = {
  QB: '#A78BFA',
  RB: '#2FBF71',
  WR: '#5B8DEF',
  TE: '#E0A63F',
  K: '#7A8299',
  'D/ST': '#7A8299',
  DEF: '#7A8299',
  DST: '#7A8299',
};

const POSITION_FILTERS = ['QB', 'RB', 'WR', 'TE', 'K', 'DST'] as const;
type PositionFilter = (typeof POSITION_FILTERS)[number];

// Normalize ESPN position strings to a filter key
const normalizePosition = (position: string): string => {
  const p = position.toUpperCase();
  if (p === 'D/ST' || p === 'DEF' || p === 'DST') return 'DST';
  return p;
};

const DraftBoard: React.FC<DraftBoardProps> = ({
  leagueId,
  onPlayerSelect,
  onTeamSelect,
}) => {
  const navigate = useNavigate();
  const [draftPicks, setDraftPicks] = useState<V1DraftPick[]>([]);
  const [draftAnalysis, setDraftAnalysis] = useState<V1DraftAnalysis | null>(null);
  const [leagueDraftAnalytics, setLeagueDraftAnalytics] = useState<LeagueDraftAnalytics | null>(null);
  const [standings, setStandings] = useState<V1Standings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'board' | 'analysis'>('board');
  const [highlightedTeamId, setHighlightedTeamId] = useState<number | null>(null);
  const [isBestDraftersCollapsed, setIsBestDraftersCollapsed] = useState(false);
  // Hovered cell for the fixed-position tooltip (escapes the scroll panel's clipping)
  const [hoveredCell, setHoveredCell] = useState<{
    pick: V1DraftPick;
    x: number;
    y: number;
    below: boolean;
  } | null>(null);

  // Client-side filters (presentation only — no new API calls)
  const [positionFilters, setPositionFilters] = useState<Set<PositionFilter>>(new Set());
  const [stealsOnly, setStealsOnly] = useState(false);
  const [bustsOnly, setBustsOnly] = useState(false);

  const fetchDraftData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get league info first to get season for standings
      const leagueData = await v1Api.leagues.getByEspnId(leagueId.toString());

      // Use the new unified endpoint for much faster loading
      const completeData = await v1Api.leagues.getDraftCompleteByEspnId(leagueId.toString());

      // Fetch standings data using the ESPN league ID and season
      const standingsData = await v1Api.analytics.getStandings(parseInt(leagueId.toString()), leagueData.season);

      setDraftPicks(completeData.draft_picks);
      setDraftAnalysis(completeData.draft_analysis);
      setLeagueDraftAnalytics(completeData.league_draft_analytics);
      setStandings(standingsData);
    } catch (err) {
      setError('Failed to load draft data');
      console.error('Draft fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchDraftData();
  }, [fetchDraftData]);

  // Index analysis by pick id for O(1) lookup
  const analysisByPickId = useMemo(() => {
    const map = new Map<number, V1DraftAnalysis['draft_picks'][number]>();
    draftAnalysis?.draft_picks.forEach((p) => map.set(p.pick_id, p));
    return map;
  }, [draftAnalysis]);

  // Unique teams in draft order (by first pick), forming the board column order
  const teamColumns = useMemo(() => {
    const seen = new Map<number, { id: number; name: string; firstOverall: number }>();
    draftPicks.forEach((pick) => {
      const existing = seen.get(pick.team.id);
      if (!existing || pick.overall_pick < existing.firstOverall) {
        seen.set(pick.team.id, { id: pick.team.id, name: pick.team.name, firstOverall: pick.overall_pick });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.firstOverall - b.firstOverall);
  }, [draftPicks]);

  const totalRounds = useMemo(
    () => Math.max(...draftPicks.map((pick) => pick.round_num), 0),
    [draftPicks]
  );

  // board[round][teamId] = pick
  const boardByRound = useMemo(() => {
    const rounds: { [round: number]: { [teamId: number]: V1DraftPick } } = {};
    draftPicks.forEach((pick) => {
      if (!rounds[pick.round_num]) rounds[pick.round_num] = {};
      rounds[pick.round_num][pick.team.id] = pick;
    });
    return rounds;
  }, [draftPicks]);

  // A cell matches the active filters (used to decide dim vs full opacity)
  const matchesFilters = useCallback(
    (pick: V1DraftPick): boolean => {
      if (positionFilters.size > 0 && !positionFilters.has(normalizePosition(pick.player.position) as PositionFilter)) {
        return false;
      }
      const analysis = analysisByPickId.get(pick.id);
      const grade = analysis?.performance.grade;
      if (stealsOnly && grade !== 'Boom') return false;
      if (bustsOnly && grade !== 'Bust') return false;
      return true;
    },
    [positionFilters, stealsOnly, bustsOnly, analysisByPickId]
  );

  const hasActiveFilters = positionFilters.size > 0 || stealsOnly || bustsOnly;

  const togglePosition = (pos: PositionFilter) => {
    setPositionFilters((prev) => {
      const next = new Set(prev);
      if (next.has(pos)) next.delete(pos);
      else next.add(pos);
      return next;
    });
  };

  const clearFilters = () => {
    setPositionFilters(new Set());
    setStealsOnly(false);
    setBustsOnly(false);
  };

  // Top-border color for a cell, from the performance grade tier
  const borderColorFor = (pick: V1DraftPick): string => {
    const grade = analysisByPickId.get(pick.id)?.performance.grade;
    if (grade === 'Boom') return PERF.exceeded;
    if (grade === 'Bust') return PERF.missed;
    return PERF.met; // Fair / Met (or ungraded)
  };

  const handleTeamNav = (teamId: number) => {
    // Prefer explicit handler if provided, else navigate to team roster route
    if (onTeamSelect) onTeamSelect(teamId);
    else navigate(`/leagues/espn/${leagueId}/teams/${teamId}`);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <GradingLoader label="Scoring every pick…" size="lg" className="py-12" />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 text-center">
        <div className="text-accent-danger mb-4">{error}</div>
        <button
          onClick={fetchDraftData}
          className="px-4 py-2 bg-accent-primary text-background-primary rounded-lg font-medium hover:brightness-110 transition-all active:translate-y-px"
        >
          Retry
        </button>
      </Card>
    );
  }

  const LegendChip: React.FC<{ color: string; label: string }> = ({ color, label }) => (
    <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
      {label}
    </span>
  );

  // Hover tooltip rendered OUTSIDE the scroll panel (position: fixed) so it
  // can't be clipped by the board's overflow container.
  const hoveredPerf = hoveredCell ? analysisByPickId.get(hoveredCell.pick.id)?.performance : undefined;
  const hoverTooltip = hoveredCell && hoveredPerf ? (() => {
    const { pick, x, y, below } = hoveredCell;
    const perf = hoveredPerf;
    const score = perf.pick_score ?? null;
    const pickGrade = pickScoreToGrade(score);
    const pos = pick.player.position;
    return (
      <div
        className="pointer-events-none fixed z-50 w-64 rounded-lg border border-fieldline bg-background-tertiary p-3 text-left shadow-xl"
        style={{
          left: x,
          top: y,
          transform: below ? 'translate(-50%, 8px)' : 'translate(-50%, calc(-100% - 8px))',
        }}
      >
        <p className="text-sm font-semibold text-text-primary mb-1">{pick.player.name}</p>
        {score !== null ? (
          <>
            <p className="font-mono text-xs text-text-secondary">
              Pick Score <span className="font-bold" style={{ color: pickGrade ? gradeToColor(pickGrade) : undefined }}>{score}</span>
              {' — '}beat {Math.round(perf.pick_score_raw ?? score)}% of {pos}s still on the board
            </p>
            <p className="font-mono text-xs text-text-secondary mt-1">
              Drafted {pos}{perf.drafted_pos_rank} of {perf.position_pool_size} · Finished {pos}{perf.finished_pos_rank}
            </p>
          </>
        ) : (
          <p className="font-mono text-xs text-text-secondary">
            Last {pos}s off the board — no alternatives left to judge against.
          </p>
        )}
        {perf.best_alternative && (
          <p className="font-mono text-xs text-text-tertiary mt-1">
            Best {pos} taken later: {perf.best_alternative.player_name} (#
            {perf.best_alternative.overall_pick}, {Number(perf.best_alternative.total_points).toFixed(1)} pts)
          </p>
        )}
        {perf.departure && (
          <p className="font-mono text-xs text-accent-warning mt-1">
            {perf.departure.verb} after Week {perf.departure.after_week}
          </p>
        )}
        <p className="font-mono text-xs text-text-tertiary mt-1">
          {Number(perf.total_points).toFixed(1)} pts · {perf.games_played} games
        </p>
      </div>
    );
  })() : null;

  // A single draft-board cell
  const BoardCell: React.FC<{ pick: V1DraftPick }> = ({ pick }) => {
    const analysis = analysisByPickId.get(pick.id);
    const dim = hasActiveFilters && !matchesFilters(pick);
    const posColor = POSITION_COLOR[pick.player.position.toUpperCase()] || '#7A8299';
    const perf = analysis?.performance;
    const score = perf?.pick_score ?? null;
    const pickGrade = pickScoreToGrade(score);

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      // Flip below the cell when there isn't room for the tooltip above
      const below = rect.top < 240;
      setHoveredCell({
        pick,
        x: rect.left + rect.width / 2,
        y: below ? rect.bottom : rect.top,
        below,
      });
    };

    return (
      <button
        type="button"
        onClick={() => onPlayerSelect?.(pick.player.id)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHoveredCell(null)}
        className="group relative flex w-full flex-col gap-1.5 rounded-md bg-background-secondary p-2.5 text-left transition-all duration-200 hover:bg-background-hover focus:outline-none focus:ring-1 focus:ring-accent-primary"
        style={{
          borderTop: `2px solid ${borderColorFor(pick)}`,
          opacity: dim ? 0.25 : 1,
        }}
      >
        <div className="flex items-center justify-between gap-1.5">
          <span className="truncate text-sm font-semibold text-text-primary leading-tight">
            {pick.player.name}
          </span>
          {/* Micro grade stamp — every pick gets graded, in red pen */}
          {pickGrade && (
            <span
              className="flex-shrink-0 font-mono text-xs font-bold px-1.5 py-0.5 rounded border"
              style={{
                color: gradeToColor(pickGrade),
                borderColor: `${gradeToColor(pickGrade)}66`,
                backgroundColor: `${gradeToColor(pickGrade)}1a`,
              }}
            >
              {pickGrade}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-1.5">
          <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: posColor }} />
            {pick.player.position}
            <span className="text-text-tertiary">· {pick.player.nfl_team}</span>
          </span>
          <span className="font-mono text-xs text-text-tertiary flex-shrink-0">
            #{pick.overall_pick}
          </span>
        </div>
        {perf && (
          <div className="flex items-center justify-between gap-1.5 font-mono text-xs">
            <span className="text-text-secondary">
              {Number(perf.total_points).toFixed(1)} pts
            </span>
            {score !== null && (
              <span
                className="text-sm font-bold"
                style={{ color: pickGrade ? gradeToColor(pickGrade) : undefined }}
              >
                {score}
              </span>
            )}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {hoverTooltip}
      {/* Best Drafters Container - collapsible */}
      <div className="relative">
        <Card className="p-4">
          <button
            onClick={() => setIsBestDraftersCollapsed(!isBestDraftersCollapsed)}
            className="flex items-center justify-between w-full mb-3 hover:bg-white/5 rounded-lg p-2 transition-colors"
          >
            <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              Best Drafters
              <span className="text-sm text-text-secondary font-normal">Ranked by Drafted Starters Points Scored</span>
            </h3>
            {isBestDraftersCollapsed ? (
              <ChevronDown className="h-5 w-5 text-text-secondary" />
            ) : (
              <ChevronUp className="h-5 w-5 text-text-secondary" />
            )}
          </button>

          {!isBestDraftersCollapsed && (
            <BestDraftersContainer
              leagueId={leagueId}
              draftAnalytics={leagueDraftAnalytics}
              standings={standings}
              loading={loading}
              onTeamSelect={(teamId) => {
                setHighlightedTeamId(teamId);
              }}
              className="border-0 bg-transparent p-0"
            />
          )}
        </Card>
      </div>

      {/* Header + view toggle */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary tracking-tight">Draft Board</h2>
          <p className="font-mono text-sm text-text-secondary">
            {draftPicks.length} picks · {totalRounds} rounds · {teamColumns.length} teams
          </p>
        </div>
        <div className="flex bg-background-tertiary rounded-lg p-1 self-start">
          <button
            onClick={() => setViewMode('board')}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              viewMode === 'board'
                ? 'bg-accent-primary text-background-primary font-medium'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Board
          </button>
          <button
            onClick={() => setViewMode('analysis')}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              viewMode === 'analysis'
                ? 'bg-accent-primary text-background-primary font-medium'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Analysis
          </button>
        </div>
      </div>

      {viewMode === 'board' && (
        <div className="space-y-4">
          {/* Legend chips */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <LegendChip color={PERF.exceeded} label="Boom" />
            <LegendChip color={PERF.met} label="Fair" />
            <LegendChip color={PERF.missed} label="Bust" />
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap items-center gap-2">
            {POSITION_FILTERS.map((pos) => {
              const active = positionFilters.has(pos);
              const color = POSITION_COLOR[pos] || '#7A8299';
              return (
                <button
                  key={pos}
                  onClick={() => togglePosition(pos)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    active
                      ? 'border-transparent text-background-primary'
                      : 'border-fieldline text-text-secondary hover:text-text-primary'
                  }`}
                  style={active ? { background: color } : undefined}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? '#0A0E1A' : color }} />
                  {pos}
                </button>
              );
            })}
            <span className="mx-1 h-4 w-px bg-fieldline" />
            <button
              onClick={() => setStealsOnly((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                stealsOnly ? 'border-transparent text-background-primary' : 'border-fieldline text-text-secondary hover:text-text-primary'
              }`}
              style={stealsOnly ? { background: PERF.exceeded } : undefined}
            >
              Steals only
            </button>
            <button
              onClick={() => setBustsOnly((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                bustsOnly ? 'border-transparent text-background-primary' : 'border-fieldline text-text-secondary hover:text-text-primary'
              }`}
              style={bustsOnly ? { background: PERF.missed } : undefined}
            >
              Busts only
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="ml-1 text-xs text-text-secondary underline underline-offset-2 hover:text-text-primary"
              >
                Clear
              </button>
            )}
          </div>

          {/* The board — scrolls horizontally inside its own container on small screens */}
          <Card className="p-0 overflow-hidden">
            {/* Internal scroll container: the board scrolls inside its own
                viewport-height panel, so headers stick to the panel, not the page */}
            <div className="overflow-auto max-h-[78vh]" onScroll={() => setHoveredCell(null)}>
              <div
                className="grid"
                style={{
                  gridTemplateColumns: `56px repeat(${teamColumns.length}, minmax(172px, 1fr))`,
                  minWidth: `${56 + teamColumns.length * 172}px`,
                }}
              >
                {/* Sticky team-name header row (sticks to the top of the scroll panel) */}
                <div className="sticky top-0 left-0 z-30 bg-background-tertiary border-b border-fieldline px-2 py-2.5 text-xs font-medium uppercase tracking-wide text-text-secondary">
                  Rd
                </div>
                {teamColumns.map((team) => (
                  <button
                    key={team.id}
                    onClick={() => handleTeamNav(team.id)}
                    title={team.name}
                    className={`sticky top-0 z-20 border-b border-l border-fieldline px-2.5 py-2.5 text-left text-sm font-medium leading-tight transition-colors hover:text-accent-primary ${
                      highlightedTeamId === team.id ? 'text-accent-primary bg-background-tertiary' : 'text-text-primary bg-background-tertiary'
                    }`}
                  >
                    <span className="block truncate">{team.name}</span>
                  </button>
                ))}

                {/* Rows: one per round */}
                {Array.from({ length: totalRounds }, (_, i) => i + 1).map((round) => (
                  <React.Fragment key={round}>
                    {/* Sticky left round rail */}
                    <div className="sticky left-0 z-10 flex items-center justify-center border-b border-fieldline bg-background-tertiary font-mono text-sm text-text-secondary">
                      {round}
                    </div>
                    {teamColumns.map((team) => {
                      const pick = boardByRound[round]?.[team.id];
                      return (
                        <div
                          key={team.id}
                          className="border-b border-l border-fieldline p-1"
                        >
                          {pick ? (
                            <BoardCell pick={pick} />
                          ) : (
                            <div className="h-full min-h-[64px] rounded-md bg-background-primary/40" />
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </Card>
        </div>
      )}

      {viewMode === 'analysis' && draftAnalysis && (
        <div className="space-y-6">
          {/* Methodology Explanation */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-3">The Pick Score</h3>
            <p className="text-text-secondary text-sm leading-relaxed">
              Every pick is scored <strong>0&ndash;100</strong>: the share of players still on the board at his
              position that he actually outscored. A 100 means nobody you could have taken instead did better.
              A 5 means almost everybody did. It grades the <strong>decision</strong>, not the player &mdash;
              measured against the real alternatives at that moment. No curve. No excuses.
              Booms scored 80+, busts scored 20 or below.
            </p>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Best Booms */}
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center" style={{ color: PERF.exceeded }}>
                <TrendingUp className="w-5 h-5 mr-2" />
                Best Booms
              </h3>
              <div className="space-y-3">
                {draftAnalysis.best_booms.slice(0, 10).map((pick) => (
                  <div
                    key={pick.pick_id}
                    className="rounded-lg bg-background-secondary p-4"
                    style={{ borderTop: `2px solid ${PERF.exceeded}` }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-start gap-3">
                        <GradeStamp
                          grade={pickScoreToGrade(pick.performance.pick_score)}
                          size="xs"
                        />
                        <div>
                          <h4 className="text-text-primary font-semibold">{pick.player.name}</h4>
                          <p className="text-text-secondary text-sm">{pick.player.position} - {pick.player.nfl_team}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold" style={{ color: PERF.exceeded }}>Pick #{pick.overall_pick}</div>
                        <div className="text-text-secondary text-sm">Round {pick.round_num}</div>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Points:</span>
                      <span className="text-text-primary font-mono font-medium">{Number(pick.performance.total_points).toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Pick Score:</span>
                      <span className="font-mono font-medium" style={{ color: PERF.exceeded }}>
                        {pick.performance.pick_score ?? '—'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Team:</span>
                      <button
                        onClick={() => handleTeamNav(pick.team.id)}
                        className="text-accent-primary hover:brightness-110 transition-colors"
                      >
                        {pick.team.name}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Worst Busts */}
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center" style={{ color: PERF.missed }}>
                <TrendingDown className="w-5 h-5 mr-2" />
                Worst Busts
              </h3>
              <div className="space-y-3">
                {draftAnalysis.worst_busts.slice(0, 10).map((pick) => (
                  <div
                    key={pick.pick_id}
                    className="rounded-lg bg-background-secondary p-4"
                    style={{ borderTop: `2px solid ${PERF.missed}` }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-start gap-3">
                        <GradeStamp
                          grade={pickScoreToGrade(pick.performance.pick_score)}
                          size="xs"
                        />
                        <div>
                          <h4 className="text-text-primary font-semibold">{pick.player.name}</h4>
                          <p className="text-text-secondary text-sm">{pick.player.position} - {pick.player.nfl_team}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold" style={{ color: PERF.missed }}>Pick #{pick.overall_pick}</div>
                        <div className="text-text-secondary text-sm">Round {pick.round_num}</div>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Points:</span>
                      <span className="text-text-primary font-mono font-medium">{Number(pick.performance.total_points).toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Pick Score:</span>
                      <span className="font-mono font-medium" style={{ color: PERF.missed }}>
                        {pick.performance.pick_score ?? '—'}
                      </span>
                    </div>
                    {pick.performance.best_alternative && (
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">Could've had:</span>
                        <span className="text-text-primary font-mono font-medium">
                          {pick.performance.best_alternative.player_name} ({Number(pick.performance.best_alternative.total_points).toFixed(1)})
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Team:</span>
                      <button
                        onClick={() => handleTeamNav(pick.team.id)}
                        className="text-accent-primary hover:brightness-110 transition-colors"
                      >
                        {pick.team.name}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftBoard;
